<template>
  <AppLayout 
    page-title="AI Assistant Playground" 
    page-subtitle="Build, train, and interact with intelligent AI agents"
  >
    <!-- AI Assistant Content -->
    <div class="space-y-8 pt-8">
      <!-- AI Agent Builder -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <!-- Agent Creation Panel -->
        <div class="lg:col-span-1">
          <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
            <h3 class="text-xl font-semibold text-white mb-4">Create AI Agent</h3>
            
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Agent Name</label>
                <input 
                  v-model="newAgent.name"
                  type="text" 
                  placeholder="e.g., Job Matcher, Safety Advisor"
                  class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Agent Type</label>
                <select 
                  v-model="newAgent.type"
                  class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="">Select type</option>
                  <option value="job-matcher">Job Matcher</option>
                  <option value="safety-advisor">Safety Advisor</option>
                  <option value="compliance-checker">Compliance Checker</option>
                  <option value="payroll-assistant">Payroll Assistant</option>
                  <option value="scheduling-optimizer">Scheduling Optimizer</option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">AI Model</label>
                <select 
                  v-model="newAgent.model"
                  class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option v-for="model in availableModels" :key="model.id" :value="model.id">
                    {{ model.name }} ({{ model.provider }})
                  </option>
                </select>
                <p class="text-xs text-slate-400 mt-1">
                  {{ availableModels.find(m => m.id === newAgent.model)?.capabilities.join(', ') }}
                </p>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
                <textarea 
                  v-model="newAgent.description"
                  rows="3"
                  placeholder="Describe what this agent should do..."
                  class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                ></textarea>
              </div>
              
              <button 
                @click="createAgent"
                :disabled="!newAgent.name || !newAgent.type || isCreating"
                class="w-full btn-primary"
              >
                <svg v-if="isCreating" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                <span v-else>Create Agent</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Agent Training & Stats -->
        <div class="lg:col-span-2">
          <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
            <h3 class="text-xl font-semibold text-white mb-4">Training & Performance</h3>
            
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div class="text-center p-4 bg-slate-700/30 rounded-xl">
                <div class="text-2xl font-bold text-emerald-400">{{ aiStats.totalAgents }}</div>
                <div class="text-sm text-slate-300">Active Agents</div>
              </div>
              <div class="text-center p-4 bg-slate-700/30 rounded-xl">
                <div class="text-2xl font-bold text-cyan-400">{{ aiStats.trainingSessions }}</div>
                <div class="text-sm text-slate-300">Training Sessions</div>
              </div>
              <div class="text-center p-4 bg-slate-700/30 rounded-xl">
                <div class="text-2xl font-bold text-amber-400">{{ aiStats.accuracy }}%</div>
                <div class="text-sm text-slate-300">Average Accuracy</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Chat Interface -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Chat History -->
        <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-xl font-semibold text-white">Chat History</h3>
            <button 
              @click="refreshData" 
              :disabled="isLoading"
              class="btn-primary text-sm"
            >
              <svg v-if="isLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
            </button>
          </div>
          
          <!-- Chat List -->
          <div class="space-y-2 max-h-96 overflow-y-auto">
            <div 
              v-for="chat in chatHistory" 
              :key="chat.id" 
              @click="selectChat(chat.id)"
              class="p-3 bg-slate-700/30 rounded-lg cursor-pointer hover:bg-slate-600/50 transition-colors"
              :class="selectedChatId === chat.id ? 'bg-emerald-500/20 border border-emerald-500/30' : ''"
            >
              <div class="flex items-center justify-between">
                <div class="flex-1 min-w-0">
                  <h4 class="text-sm font-medium text-white truncate">{{ chat.title }}</h4>
                  <p class="text-xs text-slate-400 truncate">{{ chat.lastMessage }}</p>
                  <p class="text-xs text-slate-500">{{ formatTime(chat.updatedAt) }}</p>
                </div>
                <div class="flex items-center space-x-1 ml-2">
                  <button 
                    @click.stop="deleteChat(chat.id)"
                    class="p-1 text-slate-400 hover:text-red-400 transition-colors"
                    title="Delete chat"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            
            <div v-if="chatHistory.length === 0" class="text-center py-8">
              <p class="text-slate-400 text-sm">No chat history yet</p>
              <p class="text-slate-500 text-xs">Start a conversation to see it here</p>
            </div>
          </div>
        </div>

        <!-- Chat Window -->
        <div class="lg:col-span-2 bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-xl font-semibold text-white">AI Chat</h3>
            <div class="flex items-center space-x-2">
              <button 
                @click="clearChat"
                :disabled="currentChatMessages.length === 0"
                class="btn-secondary text-sm"
                title="Clear current chat"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
                Clear
              </button>
            </div>
          </div>
          
          <!-- Chat Messages -->
          <div class="h-96 overflow-y-auto mb-4 space-y-4" ref="chatContainer">
            <div v-for="message in currentChatMessages" :key="message.id" class="flex" :class="message.sender === 'user' ? 'justify-end' : 'justify-start'">
              <div 
                class="max-w-xs lg:max-w-md px-4 py-2 rounded-lg"
                :class="message.sender === 'user' 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-slate-700/50 text-slate-200'"
              >
                <p class="text-sm">{{ message.content }}</p>
                <p class="text-xs opacity-70 mt-1">{{ formatTime(message.timestamp) }}</p>
              </div>
            </div>
          </div>
          
          <!-- Chat Input -->
          <div class="flex space-x-2">
            <input 
              v-model="newMessage"
              @keyup.enter="sendMessage"
              type="text" 
              placeholder="Type your message..."
              class="flex-1 px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
            <button 
              @click="sendMessage"
              :disabled="!newMessage.trim() || isSending"
              class="btn-primary"
            >
              <svg v-if="isSending" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Agent Management -->
        <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
          <h3 class="text-xl font-semibold text-white mb-4">My AI Agents</h3>
          
          <div class="space-y-4">
            <div v-for="agent in userAgents" :key="agent.id" class="p-4 bg-slate-700/30 rounded-xl">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <svg class="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-sm font-semibold text-white">{{ agent.name }}</h4>
                    <p class="text-xs text-slate-400">{{ agent.type }}</p>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <span :class="getAgentStatusClass(agent.status)" class="px-2 py-1 text-xs rounded-full">
                    {{ agent.status }}
                  </span>
                  <button @click="selectAgent(agent)" class="btn-primary text-sm">
                    Select
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Training History -->
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 class="text-xl font-semibold text-white mb-4">Training History</h3>
        
        <div class="space-y-4">
          <div v-for="session in trainingHistory" :key="session.id" class="p-4 bg-slate-700/30 rounded-xl">
            <div class="flex items-center justify-between">
              <div>
                <h4 class="text-sm font-semibold text-white">{{ session.agentName }}</h4>
                <p class="text-xs text-slate-400">{{ session.description }}</p>
              </div>
              <div class="text-right">
                <p class="text-sm text-emerald-400">{{ session.accuracy }}% accuracy</p>
                <p class="text-xs text-slate-400">{{ formatDate(session.date) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- AI Agent Marketplace -->
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-xl font-semibold text-white">AI Agent Marketplace</h3>
            <p class="text-slate-300">Deploy pre-trained AI agents designed specifically for oilfield contractors</p>
          </div>
          <button @click="viewFullMarketplace" class="btn-primary">
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
            View Full Marketplace
          </button>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <!-- Document Assistant -->
          <div class="bg-slate-700/30 border border-slate-600 rounded-lg p-6 hover:bg-slate-700/50 transition-all duration-200">
            <div class="flex items-center justify-between mb-4">
              <div class="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <span class="px-2 py-1 text-xs bg-emerald-500/20 text-emerald-400 rounded-full">Ready</span>
            </div>
            <h4 class="text-lg font-semibold text-white mb-2">Document Assistant</h4>
            <p class="text-sm text-slate-300 mb-4">Create, edit, and format professional documents including safety reports, compliance forms, and project documentation.</p>
            <div class="space-y-2 mb-4">
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Safety reports & incident documentation
              </div>
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Compliance forms & regulatory documents
              </div>
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Project proposals & technical specifications
              </div>
            </div>
            <button @click="deployAgent(documentAssistant)" class="w-full btn-primary">
              Deploy Agent
            </button>
          </div>

          <!-- Email Writer -->
          <div class="bg-slate-700/30 border border-slate-600 rounded-lg p-6 hover:bg-slate-700/50 transition-all duration-200">
            <div class="flex items-center justify-between mb-4">
              <div class="w-12 h-12 bg-gradient-to-r from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
              </div>
              <span class="px-2 py-1 text-xs bg-emerald-500/20 text-emerald-400 rounded-full">Ready</span>
            </div>
            <h4 class="text-lg font-semibold text-white mb-2">Email Writer</h4>
            <p class="text-sm text-slate-300 mb-4">Compose professional emails for client communication, project updates, and business correspondence.</p>
            <div class="space-y-2 mb-4">
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Client communication & proposals
              </div>
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Project status updates
              </div>
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Professional follow-ups
              </div>
            </div>
            <button @click="deployAgent(emailWriter)" class="w-full btn-primary">
              Deploy Agent
            </button>
          </div>

          <!-- Compliance Checker -->
          <div class="bg-slate-700/30 border border-slate-600 rounded-lg p-6 hover:bg-slate-700/50 transition-all duration-200">
            <div class="flex items-center justify-between mb-4">
              <div class="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-500 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <span class="px-2 py-1 text-xs bg-emerald-500/20 text-emerald-400 rounded-full">Ready</span>
            </div>
            <h4 class="text-lg font-semibold text-white mb-2">Compliance Checker</h4>
            <p class="text-sm text-slate-300 mb-4">Verify regulatory compliance, safety standards, and industry requirements for oilfield operations.</p>
            <div class="space-y-2 mb-4">
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                OSHA compliance verification
              </div>
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Environmental regulations
              </div>
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Industry best practices
              </div>
            </div>
            <button @click="deployAgent(complianceChecker)" class="w-full btn-primary">
              Deploy Agent
            </button>
          </div>

          <!-- Safety Advisor -->
          <div class="bg-slate-700/30 border border-slate-600 rounded-lg p-6 hover:bg-slate-700/50 transition-all duration-200">
            <div class="flex items-center justify-between mb-4">
              <div class="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
              </div>
              <span class="px-2 py-1 text-xs bg-emerald-500/20 text-emerald-400 rounded-full">Ready</span>
            </div>
            <h4 class="text-lg font-semibold text-white mb-2">Safety Advisor</h4>
            <p class="text-sm text-slate-300 mb-4">Provide safety guidance, risk assessments, and emergency procedures for oilfield operations.</p>
            <div class="space-y-2 mb-4">
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Risk assessment & mitigation
              </div>
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Emergency response procedures
              </div>
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Safety training recommendations
              </div>
            </div>
            <button @click="deployAgent(safetyAdvisor)" class="w-full btn-primary">
              Deploy Agent
            </button>
          </div>

          <!-- Job Matcher -->
          <div class="bg-slate-700/30 border border-slate-600 rounded-lg p-6 hover:bg-slate-700/50 transition-all duration-200">
            <div class="flex items-center justify-between mb-4">
              <div class="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6"></path>
                </svg>
              </div>
              <span class="px-2 py-1 text-xs bg-emerald-500/20 text-emerald-400 rounded-full">Ready</span>
            </div>
            <h4 class="text-lg font-semibold text-white mb-2">Job Matcher</h4>
            <p class="text-sm text-slate-300 mb-4">Match contractors with suitable job opportunities based on skills, experience, and location preferences.</p>
            <div class="space-y-2 mb-4">
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Skill-based job matching
              </div>
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Location & travel preferences
              </div>
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Salary & benefits analysis
              </div>
            </div>
            <button @click="deployAgent(jobMatcher)" class="w-full btn-primary">
              Deploy Agent
            </button>
          </div>

          <!-- Contract Analyzer -->
          <div class="bg-slate-700/30 border border-slate-600 rounded-lg p-6 hover:bg-slate-700/50 transition-all duration-200">
            <div class="flex items-center justify-between mb-4">
              <div class="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                </svg>
              </div>
              <span class="px-2 py-1 text-xs bg-emerald-500/20 text-emerald-400 rounded-full">Ready</span>
            </div>
            <h4 class="text-lg font-semibold text-white mb-2">Contract Analyzer</h4>
            <p class="text-sm text-slate-300 mb-4">Analyze contracts, identify key terms, risks, and opportunities for oilfield service agreements.</p>
            <div class="space-y-2 mb-4">
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Risk assessment & mitigation
              </div>
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Payment terms & schedules
              </div>
              <div class="flex items-center text-xs text-slate-400">
                <svg class="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                Liability & insurance clauses
              </div>
            </div>
            <button @click="deployAgent(contractAnalyzer)" class="w-full btn-primary">
              Deploy Agent
            </button>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import aiService, { AI_MODELS, OILFIELD_PROMPTS } from '@/services/aiService'
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import AppLayout from '../components/layout/AppLayout.vue'
import { useUserStore } from '../stores/userStore'

const userStore = useUserStore()
const router = useRouter()

// Reactive data
const isLoading = ref(false)
const isCreating = ref(false)
const isSending = ref(false)
const newMessage = ref('')
const selectedAgent = ref<any>(null)
const selectedChatId = ref<number | null>(null)

const chatContainer = ref<HTMLElement | null>(null)

// AI Agent creation
const newAgent = reactive({
  name: '',
  type: '',
  description: '',
  model: 'claude-sonnet-4',
  trainingData: ''
})

// Available AI models
const availableModels = ref(AI_MODELS)



// AI Statistics
const aiStats = reactive({
  totalAgents: 0,
  trainingSessions: 0,
  accuracy: 0,
  totalInteractions: 0,
  avgResponseTime: '1.2s',
  uptime: 99.8,
  satisfaction: 4.8,
  tasksCompleted: 0,
  documentsGenerated: 0
})

// User agents
const userAgents = ref<any[]>([])

// Available agents in marketplace
const availableAgents = ref<any[]>([])

// Chat history
const chatHistory = ref<any[]>([])

// Current chat messages
const currentChatMessages = ref<any[]>([])

// Training history
const trainingHistory = ref<any[]>([])

// Recent activity
const recentActivity = ref<any[]>([])

// Default AI Agents for Marketplace
const documentAssistant = {
  id: 'doc-assistant-001',
  name: 'Document Assistant',
  type: 'document-assistant',
  description: 'Create, edit, and format professional documents including safety reports, compliance forms, and project documentation.',
  status: 'ready',
  capabilities: [
    'Safety reports & incident documentation',
    'Compliance forms & regulatory documents',
    'Project proposals & technical specifications'
  ]
}

const emailWriter = {
  id: 'email-writer-001',
  name: 'Email Writer',
  type: 'email-writer',
  description: 'Compose professional emails for client communication, project updates, and business correspondence.',
  status: 'ready',
  capabilities: [
    'Client communication & proposals',
    'Project status updates',
    'Professional follow-ups'
  ]
}

const complianceChecker = {
  id: 'compliance-checker-001',
  name: 'Compliance Checker',
  type: 'compliance-checker',
  description: 'Verify regulatory compliance, safety standards, and industry requirements for oilfield operations.',
  status: 'ready',
  capabilities: [
    'OSHA compliance verification',
    'Environmental regulations',
    'Industry best practices'
  ]
}

const safetyAdvisor = {
  id: 'safety-advisor-001',
  name: 'Safety Advisor',
  type: 'safety-advisor',
  description: 'Provide safety guidance, risk assessments, and emergency procedures for oilfield operations.',
  status: 'ready',
  capabilities: [
    'Risk assessment & mitigation',
    'Emergency response procedures',
    'Safety training recommendations'
  ]
}

const jobMatcher = {
  id: 'job-matcher-001',
  name: 'Job Matcher',
  type: 'job-matcher',
  description: 'Match contractors with suitable job opportunities based on skills, experience, and location preferences.',
  status: 'ready',
  capabilities: [
    'Skill-based job matching',
    'Location & travel preferences',
    'Salary & benefits analysis'
  ]
}

const contractAnalyzer = {
  id: 'contract-analyzer-001',
  name: 'Contract Analyzer',
  type: 'contract-analyzer',
  description: 'Analyze contracts, identify key terms, risks, and opportunities for oilfield service agreements.',
  status: 'ready',
  capabilities: [
    'Risk assessment & mitigation',
    'Payment terms & schedules',
    'Liability & insurance clauses'
  ]
}

// Computed properties
const currentUser = computed(() => userStore.currentUser)



// Methods
const createAgent = async () => {
  if (!newAgent.name || !newAgent.type) return
  
  isCreating.value = true
  try {
    const agent = await aiService.createAgent({
      name: newAgent.name,
      type: newAgent.type,
      description: newAgent.description,
      model: newAgent.model
    })
    
    userAgents.value.push({
      id: agent.id,
      name: agent.name,
      type: agent.type,
      status: agent.status,
      description: agent.description,
      model: agent.model,
      progress: 100,
      trainingStatus: 'Agent ready'
    })
    
    // Reset form
    newAgent.name = ''
    newAgent.type = ''
    newAgent.description = ''
    newAgent.model = 'claude-sonnet-4'
    newAgent.trainingData = ''
    
    // Update stats
    aiStats.totalAgents++
    
    // Add to recent activity
    recentActivity.value.unshift({
      id: Date.now(),
      type: 'agent_created',
      message: `Created new AI agent: ${agent.name} using ${agent.model}`,
      timestamp: new Date()
    })
  } catch (error) {
    console.error('Error creating agent:', error)
  } finally {
    isCreating.value = false
  }
}

const selectAgent = (agent: any) => {
  selectedAgent.value = agent
  startNewChat()
}

const selectChat = (chatId: number) => {
  selectedChatId.value = chatId
  loadChatMessages(chatId)
}

const deleteChat = async (chatId: number) => {
  try {
    const response = await fetch(`/api/ai/chat/${chatId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      chatHistory.value = chatHistory.value.filter(c => c.id !== chatId)
      if (selectedChatId.value === chatId) {
        selectedChatId.value = null
        currentChatMessages.value = []
      }
    }
  } catch (error) {
    console.error('Error deleting chat:', error)
  }
}

const clearChat = () => {
  currentChatMessages.value = []
}

const startNewChat = () => {
  if (!selectedAgent.value) return
  
  const newChat = {
    id: Date.now(),
    title: `Chat with ${selectedAgent.value.name}`,
    lastMessage: 'New conversation started',
    agentStatus: selectedAgent.value.status,
    updatedAt: new Date()
  }
  
  chatHistory.value.unshift(newChat)
  selectedChatId.value = newChat.id
  currentChatMessages.value = []
}

const loadChatMessages = async (chatId: number) => {
  try {
    const response = await fetch(`/api/ai/chat/${chatId}/messages`, {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const result = await response.json()
      currentChatMessages.value = result.messages || []
    }
  } catch (error) {
    console.error('Error loading chat messages:', error)
  }
}

const sendMessage = async () => {
  if (!newMessage.value.trim() || !selectedAgent.value) return
  
  const userMessage = {
    id: Date.now(),
    content: newMessage.value,
    sender: 'user',
    timestamp: new Date()
  }
  
  currentChatMessages.value.push(userMessage)
  const messageContent = newMessage.value
  newMessage.value = ''
  isSending.value = true
  
  try {
    // Get the appropriate context based on agent type
    const context = OILFIELD_PROMPTS[selectedAgent.value.type as keyof typeof OILFIELD_PROMPTS] || OILFIELD_PROMPTS.general
    
    const aiResponse = await aiService.sendMessage(
      selectedChatId.value?.toString() || 'default',
      messageContent,
      selectedAgent.value.model || 'claude-sonnet-4',
      context
    )
    
    const responseMessage = {
      id: Date.now() + 1,
      content: aiResponse.content,
      sender: 'ai',
      timestamp: new Date(),
      model: aiResponse.model,
      tokens: aiResponse.tokens,
      cost: aiResponse.cost
    }
    
    currentChatMessages.value.push(responseMessage)
    
    // Update chat history
    const chat = chatHistory.value.find(c => c.id === selectedChatId.value)
    if (chat) {
      chat.lastMessage = responseMessage.content.substring(0, 50) + '...'
      chat.updatedAt = new Date()
    }
    
    // Update stats
    aiStats.totalInteractions++
    
    // Add to recent activity
    recentActivity.value.unshift({
      id: Date.now(),
      type: 'chat_message',
      message: `Chat with ${selectedAgent.value.name} using ${aiResponse.model}`,
      timestamp: new Date()
    })
    
    // Scroll to bottom
    await nextTick()
    if (chatContainer.value) {
      chatContainer.value.scrollTop = chatContainer.value.scrollHeight
    }
  } catch (error) {
    console.error('Error sending message:', error)
  } finally {
    isSending.value = false
  }
}

const deployAgent = async (agent: any) => {
  try {
    const response = await fetch('/api/ai/agents/deploy', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        agentId: agent.id,
        userId: userStore.currentUser?.id
      })
    })
    
    if (response.ok) {
      const result = await response.json()
      userAgents.value.push({
        id: result.agent.id,
        name: agent.name,
        type: agent.type || 'custom',
        status: 'training',
        description: agent.description,
        progress: 0,
        trainingStatus: 'Initializing deployment...'
      })
      
      // Remove from marketplace
      availableAgents.value = availableAgents.value.filter(a => a.id !== agent.id)
      
      await refreshData()
    }
  } catch (error) {
    console.error('Error deploying agent:', error)
  }
}







const getAgentStatusClass = (status: string) => {
  switch (status) {
    case 'active': return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
    case 'training': return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
    case 'inactive': return 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
    default: return 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
  }
}



const formatTime = (timestamp: Date) => {
  return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString()
}

const refreshData = async () => {
  isLoading.value = true
  try {
    // Initialize AI service
    await aiService.initialize()
    
    // Load AI statistics
    const stats = await aiService.getAIStats()
    Object.assign(aiStats, stats)
    
    // Load user agents
    const agents = await aiService.getAgents()
    userAgents.value = agents
    
    // Load available models
    const models = await aiService.getAvailableModels()
    availableModels.value = models
    
    // Load conversations
    const conversations = await aiService.getConversations()
    chatHistory.value = conversations.map(conv => ({
      id: conv.id,
      title: `Chat with ${conv.agentId}`,
      lastMessage: conv.messages[conv.messages.length - 1]?.content.substring(0, 50) + '...' || 'New conversation',
      agentStatus: 'active',
      updatedAt: new Date(conv.updatedAt)
    }))
    
    // Load training history (mock data for now)
    trainingHistory.value = [
      {
        id: 1,
        agentName: 'Safety Advisor',
        model: 'claude-sonnet-4',
        status: 'completed',
        accuracy: 98,
        duration: '2h 15m',
        timestamp: new Date()
      },
      {
        id: 2,
        agentName: 'Compliance Checker',
        model: 'gpt-5',
        status: 'completed',
        accuracy: 96,
        duration: '1h 45m',
        timestamp: new Date(Date.now() - 86400000)
      }
    ]
    
    // Load recent activity
    recentActivity.value = [
      {
        id: 1,
        type: 'agent_created',
        message: 'Created Safety Advisor using Claude Sonnet 4',
        timestamp: new Date()
      },
      {
        id: 2,
        type: 'chat_message',
        message: 'Chat with Compliance Checker using GPT-5',
        timestamp: new Date(Date.now() - 3600000)
      },
      {
        id: 3,
        type: 'agent_trained',
        message: 'Trained Operations Optimizer using Claude Sonnet 4',
        timestamp: new Date(Date.now() - 7200000)
      }
    ]
  } catch (error) {
    console.error('Error refreshing data:', error)
  } finally {
    isLoading.value = false
  }
}

// Watch for chat changes to auto-scroll
watch(currentChatMessages, async () => {
  await nextTick()
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight
  }
})

// Lifecycle
onMounted(async () => {
  try {
    await userStore.loadUserFromStorage()
    await refreshData()
    console.log('AI Assistant page loaded successfully')
  } catch (error) {
    console.error('AI Assistant page mount error:', error)
  }
})

const viewFullMarketplace = () => {
  router.push('/marketplace')
}
</script>

<style scoped>
.btn-primary {
  @apply px-6 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-medium rounded-lg hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed;
}
</style>
